import { PlatformQueryNotice } from "../components/PlatformQueryNotice";
import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Copy, Eye, EyeOff, RefreshCw, ShieldOff } from "lucide-react";
import {
  ApiError,
  getSuperAdminLiveConfig,
  getSuperAdminLiveDiagnostics,
  killSuperAdminStream,
  unblockSuperAdminStream,
  updateAdminSettings,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import type {
  AdminDiagnostics,
  AdminLiveConfig,
  AdminLiveDiagnostics,
  AdminPerformance,
  AdminStreamBlock,
  AdminStreamSession,
} from "@/lib/types";
import {
  useOperatorDiagnostics,
  useOperatorPerformance,
  useOperatorStreams,
} from "../hooks/useOperatorData";
import { AdminIconButton } from "@/components/console/AdminIconButton";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";

/**
 * `/super-admin/live` — platform live-infrastructure cockpit
 * (admin-console-split PR 4).
 *
 * The PLATFORM half of `features/admin/pages/LivePage.tsx`: master live
 * enable, RTMP public base URL, singleton stream key/path, encoder load,
 * cross-club viewer sessions (with Kill), kill-blocks (with Unblock), and
 * MediaMTX ingest diagnostics. Everything club-scoped — the per-team
 * broadcast-readiness strip and per-team stream-key rotation — stays in
 * the club admin console at `/admin/live`.
 *
 * Data flows through `/api/super-admin/*` (private `super-*` unscoped
 * cache domains); writes still go through `PUT /api/admin/settings`,
 * which is super-admin-gated server-side. Inner data-testids are kept
 * verbatim from the source page so the live E2E specs can migrate
 * without renames.
 */

/** Super-admin sessions carry optional cross-club attribution. */
type OperatorStreamSession = AdminStreamSession & {
  team_slug?: string | null;
  club_slug?: string | null;
};

export function LiveInfraPage() {
  // Swap the shell's 10 s streams cadence to 5 s while on this route —
  // the shared cache key means both intervals coexist (last one wins).
  const streamsQuery = useOperatorStreams({ refetchIntervalMs: 5_000 });
  const perfQuery = useOperatorPerformance({ refetchIntervalMs: 5_000 });
  const diagnosticsQuery = useOperatorDiagnostics({
    refetchIntervalMs: 10_000,
  });
  const liveDiagnosticsQuery = useQuery({
    queryKey: unscopedKey("super-live-diagnostics"),
    queryFn: getSuperAdminLiveDiagnostics,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
  const configQuery = useQuery({
    queryKey: unscopedKey("super-live-config"),
    queryFn: getSuperAdminLiveConfig,
  });

  const liveSessions: OperatorStreamSession[] = (
    streamsQuery.data?.active ?? []
  ).filter((s) => s.kind === "live");
  const blocks = streamsQuery.data?.blocks ?? [];

  return (
    <div data-testid="super-admin-live-infra" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Platform"
        title="Live Infrastructure"
        description="MediaMTX ingest, encoder load, and viewer sessions across every club. Per-team stream keys are rotated from each club's own admin console."
        meta={
          perfQuery.isFetching || streamsQuery.isFetching
            ? "Refreshing..."
            : "Auto-refreshes every 5 s"
        }
      />

      <PlatformQueryNotice
        pending={configQuery.isPending}
        error={configQuery.error}
        label="ingest configuration"
        retry={configQuery.refetch}
      />
      <PlatformQueryNotice
        pending={streamsQuery.isPending}
        error={streamsQuery.error}
        label="viewer sessions"
        retry={streamsQuery.refetch}
      />
      <PlatformQueryNotice
        pending={perfQuery.isPending}
        error={perfQuery.error}
        label="encoder telemetry"
        retry={perfQuery.refetch}
      />
      <div className="platform-two-col">
        {configQuery.data ? <IngestConfigCard query={configQuery} /> : null}
        <div className="flex flex-col gap-3 min-w-0">
          {streamsQuery.data && configQuery.data && perfQuery.data ? (
            <OnAirAndThroughputRow
              sessions={liveSessions}
              perf={perfQuery.data}
              configEnabled={!!configQuery.data.enabled}
            />
          ) : null}
          {perfQuery.data ? <EncoderLoadTile perf={perfQuery.data} /> : null}
        </div>
      </div>
      <PlatformQueryNotice
        pending={liveDiagnosticsQuery.isPending}
        error={liveDiagnosticsQuery.error}
        label="live diagnostics"
        retry={liveDiagnosticsQuery.refetch}
      />
      {liveDiagnosticsQuery.data ? (
        <LiveDiagnosticsBlock
          diagnostics={diagnosticsQuery.data}
          liveDiagnostics={liveDiagnosticsQuery.data}
          liveDiagnosticsFetching={liveDiagnosticsQuery.isFetching}
          liveDiagnosticsError={liveDiagnosticsQuery.error}
          onRefresh={() => void liveDiagnosticsQuery.refetch()}
        />
      ) : null}
      {streamsQuery.data ? (
        <>
          <ViewersList sessions={liveSessions} />
          <BlocksList blocks={blocks} />
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ingest config card — master enable + RTMP base URL + singleton key + offline
// ---------------------------------------------------------------------------

function IngestConfigCard({
  query,
}: {
  query: ReturnType<typeof useQuery<AdminLiveConfig>>;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const config = query.data;
  const [revealKey, setRevealKey] = useState(false);
  const [offlineDraft, setOfflineDraft] = useState<string | null>(null);
  const [rtmpDraft, setRtmpDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const saveSettings = useMutation({
    mutationFn: (body: Record<string, unknown>) => updateAdminSettings(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("super-live-config"),
      });
      setOfflineDraft(null);
      setRtmpDraft(null);
      toast.success("Ingest config saved");
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't save ingest config", {
        description: formatErr(err),
      });
    },
  });

  return (
    <section
      data-testid="admin-live-ingest"
      className="platform-panel flex flex-col gap-3 h-fit"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Ingest
        </h3>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {query.isLoading
            ? "Loading…"
            : config?.enabled
              ? "Enabled"
              : "Disabled"}
        </span>
      </header>

      {error ? (
        <p
          data-testid="admin-live-ingest-error"
          className="text-destructive-text font-mono text-xs"
        >
          {error}
        </p>
      ) : null}

      <button
        type="button"
        onClick={() => {
          setError(null);
          saveSettings.mutate({ live_enabled: config?.enabled ? "0" : "1" });
        }}
        disabled={query.isLoading || saveSettings.isPending}
        data-testid="admin-live-enabled"
        data-active={!!config?.enabled ? "true" : undefined}
        className="self-start rounded-control border px-3 py-1.5 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {config?.enabled ? "Live enabled" : "Live disabled"}
      </button>

      <div className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          RTMP base URL
        </span>
        <input
          type="text"
          value={rtmpDraft ?? config?.rtmp_public_url ?? ""}
          onChange={(e) => setRtmpDraft(e.target.value)}
          placeholder="rtmp://stream.example.com/live"
          data-testid="admin-live-rtmp-url"
          className="rounded-control border border-rule bg-field px-2 py-1.5 text-[0.65rem] font-mono text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <p className="text-[0.6rem] text-ink-muted">
          Base URL only — the ingest path is appended automatically (e.g.{" "}
          <code>rtmp://stream.example.com/live</code> becomes{" "}
          <code>rtmp://stream.example.com/live/&lt;team-slug&gt;</code>).
          Applies platform-wide; per-team keys rotate in each club&rsquo;s admin
          console.
        </p>
        {rtmpDraft !== null ? (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => {
                setError(null);
                saveSettings.mutate({
                  live_rtmp_public_url: rtmpDraft.trim(),
                });
              }}
              disabled={saveSettings.isPending}
              data-testid="admin-live-rtmp-url-save"
              className="rounded-control border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setRtmpDraft(null)}
              className="rounded-control border border-rule text-ink-muted hover:text-ink hover:bg-field px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider"
            >
              Cancel
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Stream key (singleton)
        </span>
        <div className="flex items-center gap-1">
          <code
            data-testid="admin-live-stream-key"
            data-revealed={revealKey ? "true" : "false"}
            className="break-all rounded-inset border border-rule/60 bg-elevated px-2 py-1.5 text-[0.65rem] font-mono text-ink grow"
          >
            {revealKey
              ? config?.stream_key || "—"
              : config?.stream_key
                ? "•".repeat(Math.min(24, config.stream_key.length))
                : "—"}
          </code>
          <AdminIconButton
            visibleLabel
            icon={revealKey ? EyeOff : Eye}
            label={revealKey ? "Hide stream key" : "Show stream key"}
            onClick={() => setRevealKey((v) => !v)}
            data-testid="admin-live-stream-key-toggle"
          />
          <AdminIconButton
            visibleLabel
            icon={Copy}
            label="Copy stream key"
            disabled={!config?.stream_key}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(config?.stream_key ?? "");
                toast.success("Stream key copied");
              } catch {
                toast.error("Couldn’t copy stream key");
              }
            }}
            data-testid="admin-live-stream-key-copy"
          />
        </div>
        <p
          data-testid="admin-live-stream-path"
          className="text-[0.6rem] font-mono text-ink-muted"
        >
          Publish path:{" "}
          <code className="text-ink">
            {revealKey
              ? config?.stream_path || "—"
              : redactStreamPath(config?.stream_path)}
          </code>
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Offline message
        </span>
        <textarea
          value={offlineDraft ?? config?.offline_message ?? ""}
          onChange={(e) => setOfflineDraft(e.target.value)}
          rows={3}
          data-testid="admin-live-offline-message"
          className="rounded-control border border-rule bg-field px-2 py-1.5 text-xs text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {offlineDraft !== null ? (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => {
                setError(null);
                saveSettings.mutate({ live_offline_message: offlineDraft });
              }}
              disabled={saveSettings.isPending}
              data-testid="admin-live-offline-save"
              className="rounded-control border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setOfflineDraft(null)}
              className="rounded-control border border-rule text-ink-muted hover:text-ink hover:bg-field px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider"
            >
              Cancel
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ON-AIR pill + throughput sparkline
// ---------------------------------------------------------------------------

function OnAirAndThroughputRow({
  sessions,
  perf,
  configEnabled,
}: {
  sessions: OperatorStreamSession[];
  perf: AdminPerformance | undefined;
  configEnabled: boolean;
}) {
  const onAir =
    configEnabled && sessions.length > 0
      ? "on-air"
      : configEnabled
        ? "standby"
        : "off-air";
  const liveCount = sessions.length;
  const samples = perf?.throughput?.samples ?? [];
  const lastBps = Number(perf?.throughput?.last?.bps_live_out ?? 0);
  const avgBps = Number(perf?.throughput?.avg_live_bps_30s ?? 0);

  return (
    <section data-testid="admin-live-onair-row" className="platform-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <OnAirPill state={onAir} viewers={liveCount} />
        <div className="flex flex-col items-end">
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Live throughput
          </span>
          <span
            data-testid="admin-live-throughput"
            className="text-sm font-mono text-ink"
          >
            {formatBps(lastBps)}{" "}
            <span className="text-ink-muted">
              · 30s avg {formatBps(avgBps)}
            </span>
          </span>
        </div>
      </div>
      <ThroughputSparkline
        samples={samples.map((s) => Number(s.bps_live_out ?? 0))}
      />
    </section>
  );
}

function OnAirPill({
  state,
  viewers,
}: {
  state: "off-air" | "standby" | "on-air";
  viewers: number;
}) {
  const tone =
    state === "on-air"
      ? "border-destructive/60 text-destructive-text bg-destructive/10"
      : state === "standby"
        ? "border-accent text-accent-text bg-accent/10"
        : "border-rule text-ink-muted";
  const label =
    state === "on-air" ? "ON AIR" : state === "standby" ? "STANDBY" : "OFF AIR";
  return (
    <span
      data-testid="admin-live-onair-pill"
      data-state={state}
      className={`inline-flex items-center gap-2 rounded-pill border px-3 py-1 text-xs font-display uppercase tracking-wider ${tone}`}
    >
      <span className="size-1.5 rounded-full bg-current" />
      <span>{label}</span>
      {state === "on-air" ? (
        <span className="font-mono text-[0.65rem]">
          · {viewers} viewer{viewers === 1 ? "" : "s"}
        </span>
      ) : null}
    </span>
  );
}

function ThroughputSparkline({ samples }: { samples: number[] }) {
  if (samples.length === 0) {
    return (
      <p className="mt-2 text-[0.6rem] font-mono text-ink-muted">
        No throughput samples yet.
      </p>
    );
  }
  const data = samples.slice(-60);
  const max = Math.max(...data, 1);
  const W = 600;
  const H = 40;
  const step = data.length > 1 ? W / (data.length - 1) : W;
  const points = data
    .map((v, i) => `${(i * step).toFixed(1)},${(H - (v / max) * H).toFixed(1)}`)
    .join(" ");
  return (
    <svg
      data-testid="admin-live-throughput-sparkline"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="mt-2 w-full h-10"
      aria-label="Live throughput last 60 samples"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        points={points}
        className="text-accent-text"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Encoder load tile
// ---------------------------------------------------------------------------

function EncoderLoadTile({ perf }: { perf: AdminPerformance | undefined }) {
  const host = (perf?.host ?? {}) as Record<string, unknown>;
  const cpu = Number(host.cpu_percent ?? 0);
  const mem = Number(getNested(host, ["memory", "percent"]) ?? 0);
  const gpu = Number(host.intel_gpu_busy_pct ?? 0);
  const txBps = Number(getNested(host, ["net", "bps_tx"]) ?? 0);
  const vodAvgBps = Number(perf?.throughput?.avg_vod_bps_30s ?? 0);
  const concurrency = Number(perf?.transcode?.concurrency_limit ?? 0);

  return (
    <section
      data-testid="admin-live-encoder-tile"
      className="grid grid-cols-2 lg:grid-cols-3 gap-2 rounded-panel border border-rule bg-surface p-3"
    >
      <Stat label="CPU" value={`${cpu.toFixed(0)}%`} />
      <Stat label="iGPU" value={`${gpu.toFixed(0)}%`} />
      <Stat label="Memory" value={`${mem.toFixed(0)}%`} />
      <Stat label="NIC TX" value={formatBps(txBps)} />
      <Stat label="VOD avg" value={formatBps(vodAvgBps)} />
      <Stat label="Encoder slots" value={String(concurrency)} />
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className="text-sm font-mono text-ink">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Viewers list (with Kill)
// ---------------------------------------------------------------------------

function ViewersList({ sessions }: { sessions: OperatorStreamSession[] }) {
  const collection = useCollection(sessions, {
    scope: "platform-viewers",
    initialPageSize: 10,
    text: (session) =>
      `${session.ip} ${session.club_slug} ${session.team_slug} ${session.session_id}`,
  });
  const [killTarget, setKillTarget] = useState<OperatorStreamSession | null>(
    null,
  );
  return (
    <section
      data-testid="admin-live-viewers"
      className="platform-panel flex flex-col gap-2"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Active viewers
        </h3>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {sessions.length} live
        </span>
      </header>
      <CollectionToolbar
        collection={collection}
        label="viewer sessions"
        searchPlaceholder="Viewer, club or team"
      />
      {collection.items.length === 0 ? (
        <p
          data-testid="admin-live-viewers-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No live viewers connected.
        </p>
      ) : (
        <div className="platform-table-wrap">
          <table className="platform-table">
            <thead>
              <tr>
                <th>Viewer</th>
                <th>Club / team</th>
                <th>Transferred</th>
                <th>Duration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {collection.items.map((s) => (
                <tr
                  key={s.session_id}
                  data-testid="admin-live-viewer-row"
                  data-session-id={s.session_id}
                >
                  <td data-label="Viewer">
                    <div className="platform-cell-main">
                      <strong>{s.ip}</strong>
                      <span>{s.geo_country || "—"}</span>
                    </div>
                  </td>
                  <td
                    data-label="Club / team"
                    data-testid="admin-live-viewer-attribution"
                  >
                    {sessionAttribution(s) || "—"}
                  </td>
                  <td data-label="Transferred">
                    {formatBytesShort(Number(s.bytes_sent ?? 0))}
                  </td>
                  <td data-label="Duration">
                    {Math.round(Number(s.duration_seconds ?? 0))}s active ·{" "}
                    {Math.round(Number(s.idle_seconds ?? 0))}s idle
                  </td>
                  <td data-label="Actions">
                    {" "}
                    <AdminIconButton
                      visibleLabel
                      icon={Ban}
                      label="Kill viewer session"
                      onClick={() => setKillTarget(s)}
                      data-testid="admin-live-viewer-kill"
                      tone="destructive"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CollectionPagination collection={collection} label="viewer sessions" />

      {killTarget ? (
        <KillViewerConfirm
          session={killTarget}
          onClose={() => setKillTarget(null)}
        />
      ) : null}
    </section>
  );
}

/** "club / team" attribution when the platform payload carries it. */
function sessionAttribution(s: OperatorStreamSession): string | null {
  if (s.club_slug && s.team_slug) return `${s.club_slug} / ${s.team_slug}`;
  return s.club_slug || s.team_slug || null;
}

function KillViewerConfirm({
  session,
  onClose,
}: {
  session: OperatorStreamSession;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const kill = useMutation({
    mutationFn: () => killSuperAdminStream(session.session_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("super-streams"),
      });
      toast.success("Session killed");
      onClose();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't kill session", { description: formatErr(err) });
    },
  });
  return (
    <ConfirmDialog
      title="Kill this viewer session?"
      message={
        <>
          <p>
            Disconnects <strong>{session.ip}</strong> and short-lists the (ip,
            target) for ~30 s so they can't immediately reconnect.
          </p>
          {error ? (
            <p
              data-testid="admin-live-kill-error"
              className="mt-2 text-destructive-text font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={kill.isPending ? "Killing…" : "Kill session"}
      confirmDisabled={kill.isPending}
      danger
      onConfirm={() => {
        setError(null);
        kill.mutate();
      }}
      onClose={onClose}
      data-testid="admin-live-kill-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// Blocks list (with Unblock)
// ---------------------------------------------------------------------------

function BlocksList({ blocks }: { blocks: AdminStreamBlock[] }) {
  const [unblockTarget, setUnblockTarget] = useState<AdminStreamBlock | null>(
    null,
  );

  // Always render the section so the testid is stable; the body
  // collapses to an empty-state when there are no blocks.
  return (
    <section
      data-testid="admin-live-blocks"
      className="platform-panel flex flex-col gap-2"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Blocked
        </h3>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {blocks.length} active
        </span>
      </header>
      {blocks.length === 0 ? (
        <p
          data-testid="admin-live-blocks-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No active blocks.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {blocks.map((b, i) => (
            <li
              key={`${b.ip}-${b.kind}-${i}`}
              data-testid="admin-live-block-row"
              className="grid grid-cols-[1fr_auto] gap-2 items-center rounded-inset border border-rule/60 bg-elevated px-2 py-1.5"
            >
              <div className="flex flex-col text-[0.6rem] font-mono text-ink-muted min-w-0">
                <span className="text-ink truncate">
                  {b.ip} <span className="text-ink-muted">· {b.kind}</span>
                </span>
                <span>
                  {b.match_id ? `match ${b.match_id}` : "live stream"}
                  {b.slot ? ` · ${b.slot}` : ""}
                  {b.remaining_seconds !== undefined
                    ? ` · expires in ${Math.round(b.remaining_seconds)}s`
                    : ""}
                </span>
              </div>
              <AdminIconButton
                visibleLabel
                icon={ShieldOff}
                label="Unblock stream entry"
                onClick={() => setUnblockTarget(b)}
                data-testid="admin-live-block-unblock"
              />
            </li>
          ))}
        </ul>
      )}
      {unblockTarget ? (
        <UnblockConfirm
          block={unblockTarget}
          onClose={() => setUnblockTarget(null)}
        />
      ) : null}
    </section>
  );
}

function UnblockConfirm({
  block,
  onClose,
}: {
  block: AdminStreamBlock;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const unblock = useMutation({
    mutationFn: () =>
      unblockSuperAdminStream({
        ip: block.ip,
        kind: (block.kind || "live") as "live" | "vod-hls" | "vod-mp4",
        match_id: block.match_id ?? null,
        slot: block.slot ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("super-streams"),
      });
      toast.success("IP unblocked");
      onClose();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't unblock IP", { description: formatErr(err) });
    },
  });
  return (
    <ConfirmDialog
      title="Unblock this entry?"
      message={
        <>
          <p>
            Allows <strong>{block.ip}</strong> to reconnect to{" "}
            <strong>
              {block.match_id
                ? `${block.match_id}/${block.slot ?? "live"}`
                : "live"}
            </strong>{" "}
            immediately.
          </p>
          {error ? (
            <p
              data-testid="admin-live-unblock-error"
              className="mt-2 text-destructive-text font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={unblock.isPending ? "Unblocking…" : "Unblock"}
      confirmDisabled={unblock.isPending}
      onConfirm={() => {
        setError(null);
        unblock.mutate();
      }}
      onClose={onClose}
      data-testid="admin-live-unblock-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// Live diagnostics block (MediaMTX)
// ---------------------------------------------------------------------------

function LiveDiagnosticsBlock({
  diagnostics,
  liveDiagnostics,
  liveDiagnosticsFetching,
  liveDiagnosticsError,
  onRefresh,
}: {
  diagnostics: AdminDiagnostics | undefined;
  liveDiagnostics: AdminLiveDiagnostics | undefined;
  liveDiagnosticsFetching: boolean;
  liveDiagnosticsError: unknown;
  onRefresh: () => void;
}) {
  const liveEvents = useMemo(
    () => liveActivityEvents(diagnostics),
    [diagnostics],
  );
  const publisher = liveDiagnostics?.publisher;
  const publisherReady = Boolean(publisher?.ready);
  const connections = liveDiagnostics?.rtmp_connections ?? [];
  const rejections = (liveDiagnostics?.recent_rejections ?? []).slice(0, 5);
  const otherPaths = (liveDiagnostics?.paths ?? []).filter(
    (path) =>
      String(path.name ?? "") !== String(liveDiagnostics?.stream_path ?? ""),
  );

  return (
    <section
      data-testid="admin-live-diagnostics"
      className="flex flex-col gap-3 rounded-panel border border-rule bg-surface p-3"
    >
      <header className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Diagnostics
          </h3>
          <p className="text-[0.65rem] font-mono text-ink-muted">
            MediaMTX ingest health + live stream activity
          </p>
        </div>
        <AdminIconButton
          visibleLabel
          icon={RefreshCw}
          label={
            liveDiagnosticsFetching ? "Checking diagnostics" : "Run diagnostics"
          }
          onClick={onRefresh}
          disabled={liveDiagnosticsFetching}
          data-testid="admin-live-diagnostics-refresh"
          size="md"
        />
      </header>

      {liveDiagnosticsError ? (
        <p
          data-testid="admin-live-diagnostics-error"
          className="text-destructive-text font-mono text-xs"
        >
          {formatErr(liveDiagnosticsError)}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <DiagnosticPill
          testid="admin-live-mediamtx-pill"
          tone={liveDiagnostics?.reachable ? "good" : "bad"}
          label={
            liveDiagnostics?.reachable
              ? "MediaMTX reachable"
              : "MediaMTX unreachable"
          }
        />
        <DiagnosticPill
          testid="admin-live-publisher-pill"
          tone={publisherReady ? "good" : "warn"}
          label={
            publisher
              ? `Publisher ${publisherReady ? "ready" : "not ready"}${publisher.bytes_received != null ? ` · ${formatBytesShort(Number(publisher.bytes_received))} in` : ""}`
              : "No publisher on configured path"
          }
        />
        <DiagnosticPill
          testid="admin-live-stream-path-pill"
          tone="muted"
          label={`Path ${redactStreamPath(liveDiagnostics?.stream_path)}`}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
        <DiagnosticList
          title="RTMP connections"
          count={connections.length}
          testid="admin-live-rtmp-connections"
        >
          {connections.length === 0 ? (
            <DiagEmpty copy="No active RTMP connections." />
          ) : (
            connections.map((conn, index) => (
              <DiagnosticRow
                key={`${String(conn.remote_addr ?? "?")}-${index}`}
                testid="admin-live-rtmp-row"
              >
                <code>{String(conn.remote_addr ?? "?")}</code>
                <span>state={String(conn.state ?? "unknown")}</span>
                <span>path={redactStreamPath(conn.path)}</span>
                <span>
                  in={formatBytesShort(Number(conn.bytes_received ?? 0))}
                </span>
                <span>
                  out={formatBytesShort(Number(conn.bytes_sent ?? 0))}
                </span>
              </DiagnosticRow>
            ))
          )}
        </DiagnosticList>

        <DiagnosticList
          title="Recent auth rejections"
          count={rejections.length}
          testid="admin-live-auth-rejections"
        >
          {rejections.length === 0 ? (
            <DiagEmpty copy="No recent auth rejections." />
          ) : (
            rejections.map((rej, index) => (
              <DiagnosticRow
                key={`${String(rej.ts ?? "")}-${index}`}
                testid="admin-live-auth-rejection-row"
              >
                <code>{String(rej.ts ?? "")}</code>
                <span>ip={String(rej.ip ?? "?")}</span>
                <span>
                  {String(rej.action ?? "?")}/{String(rej.protocol ?? "?")}
                </span>
                <span>path={redactStreamPath(rej.path)}</span>
                <strong>{String(rej.reason ?? "Rejected")}</strong>
              </DiagnosticRow>
            ))
          )}
        </DiagnosticList>
      </div>

      {otherPaths.length > 0 ? (
        <DiagnosticList
          title="Other MediaMTX paths"
          count={otherPaths.length}
          testid="admin-live-other-paths"
        >
          {otherPaths.map((path, index) => (
            <DiagnosticRow
              key={`${String(path.name ?? "")}-${index}`}
              testid="admin-live-path-row"
            >
              <code>{redactStreamPath(path.name)}</code>
              <span>ready={path.ready ? "yes" : "no"}</span>
            </DiagnosticRow>
          ))}
        </DiagnosticList>
      ) : null}

      <DiagnosticList
        title="Live activity"
        count={liveEvents.length}
        testid="admin-live-activity"
      >
        {liveEvents.length === 0 ? (
          <DiagEmpty copy="No live stream activity in the recent feed." />
        ) : (
          liveEvents.map((event, index) => (
            <DiagnosticRow
              key={`${event.timestamp}-${event.event_type}-${index}`}
              testid="admin-live-activity-row"
              severity={event.severity}
            >
              <span>{formatTime(event.timestamp)}</span>
              <span>{event.event_type}</span>
              <span>{event.message || "Live event"}</span>
              {event.actor ? <span>by {event.actor}</span> : null}
            </DiagnosticRow>
          ))
        )}
      </DiagnosticList>

      <p className="rounded-inset border border-rule/60 bg-elevated px-2 py-1.5 text-[0.6rem] font-mono text-ink-muted">
        Hint: RTMP state <code>read</code> means the handshake completed;{" "}
        <code>idle</code> or <code>preRead</code> means the camera opened the
        socket but has not published yet.
      </p>
    </section>
  );
}

function DiagnosticPill({
  testid,
  tone,
  label,
}: {
  testid: string;
  tone: "good" | "warn" | "bad" | "muted";
  label: string;
}) {
  const classes = {
    good: "border-good/50 text-good-text bg-good/10",
    warn: "border-accent/50 text-accent-text bg-accent/10",
    bad: "border-destructive/50 text-destructive-text bg-destructive/10",
    muted: "border-rule text-ink-muted bg-elevated",
  }[tone];
  return (
    <span
      data-testid={testid}
      data-tone={tone}
      className={`rounded-control border px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider ${classes}`}
    >
      {label}
    </span>
  );
}

function DiagnosticList({
  title,
  count,
  testid,
  children,
}: {
  title: string;
  count: number;
  testid: string;
  children: ReactNode;
}) {
  return (
    <div
      data-testid={testid}
      className="flex flex-col gap-1.5 rounded-inset border border-rule/60 bg-elevated p-2"
    >
      <div className="flex items-baseline justify-between">
        <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {title}
        </h4>
        <span className="text-[0.6rem] font-mono text-ink-muted">{count}</span>
      </div>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

function DiagnosticRow({
  testid,
  severity,
  children,
}: {
  testid: string;
  severity?: string;
  children: ReactNode;
}) {
  const tone =
    severity === "warning"
      ? "border-accent/50"
      : severity === "error"
        ? "border-destructive/50"
        : "border-rule/60";
  return (
    <div
      data-testid={testid}
      data-severity={severity ?? "info"}
      className={`flex flex-wrap gap-x-2 gap-y-1 rounded-inset border ${tone} bg-surface px-2 py-1.5 text-[0.6rem] font-mono text-ink-muted`}
    >
      {children}
    </div>
  );
}

function DiagEmpty({ copy }: { copy: string }) {
  return <p className="text-[0.6rem] font-mono text-ink-muted">{copy}</p>;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function liveActivityEvents(
  diagnostics: AdminDiagnostics | undefined,
): AdminDiagnostics["recent_activity"] {
  return (diagnostics?.recent_activity ?? [])
    .filter((event) => {
      const kind = String(event.metadata?.kind ?? "").toLowerCase();
      const type = event.event_type.toLowerCase();
      return (
        kind === "live" ||
        type.startsWith("live.") ||
        type === "stream.killed" ||
        (type.startsWith("stream.") && kind === "live")
      );
    })
    .slice(0, 8);
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}

function formatBps(bps: number): string {
  if (!Number.isFinite(bps) || bps <= 0) return "0 bps";
  const units = ["bps", "Kbps", "Mbps", "Gbps"];
  let v = bps;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function formatBytesShort(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function formatTime(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function redactStreamPath(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "—";
  const parts = raw.split("/").filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return "••••";
  return `${parts[0]}/••••`;
}

function getNested(obj: Record<string, unknown>, path: string[]): unknown {
  let cur: unknown = obj;
  for (const k of path) {
    if (
      cur &&
      typeof cur === "object" &&
      k in (cur as Record<string, unknown>)
    ) {
      cur = (cur as Record<string, unknown>)[k];
    } else {
      return undefined;
    }
  }
  return cur;
}
